import React from 'react'
import {
  PiggyBank,
  CheckCircle2,
  Package,
  ShoppingCart,
  DollarSign,
  AlertTriangle
} from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'

const CostoYMantenimientoCard = ({ costoYMantenimiento }) => {
  if (!costoYMantenimiento) return null

  const { gastosIniciales, gastosMensuales } = costoYMantenimiento

  // Reusable subcomponent for rendering array bullet points
  const BulletList = ({ items = [], iconColor = 'text-emerald-600', bgColor = 'bg-emerald-50/50' }) => (
    <div className="space-y-2">
      {items.map((item, idx) => (
        <div
          key={idx}
          className={`flex items-start gap-2 p-2 rounded-lg ${bgColor}`}
        >
          <CheckCircle2 className={`h-4 w-4 mt-0.5 flex-shrink-0 ${iconColor}`} />
          <p className="text-sm sm:text-base text-gray-700 leading-relaxed">{item}</p>
        </div>
      ))}
    </div>
  )

  // Reusable subcomponent for key-value lists (e.g., monthly cost breakdown)
  const KeyValueList = ({ data = {}, iconColor = 'text-emerald-600', bgColor = 'bg-white' }) => (
    <div className="grid gap-3">
      {Object.entries(data).map(([key, value], idx) => (
        <div
          key={idx}
          className={`flex items-start gap-2 border border-emerald-50 hover:border-emerald-200 p-3 rounded-lg transition-colors ${bgColor}`}
        >
          <CheckCircle2 className={`h-4 w-4 mt-0.5 flex-shrink-0 ${iconColor}`} />
          <div>
            <p className="text-base font-medium text-emerald-700 capitalize">{key}</p>
            <p className="text-sm sm:text-base text-gray-700">{value}</p>
          </div>
        </div>
      ))}
    </div>
  )

  return (
    <Card className="overflow-hidden bg-white">
      {/* Header */}
      <CardHeader className="border-b bg-gradient-to-r from-emerald-500/10 to-transparent p-4 sm:p-5">
        <div className="flex items-center gap-3">
          <div className="p-2 sm:p-2.5 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg shadow-sm">
            <PiggyBank className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
          </div>
          <div>
            <CardTitle className="text-xl sm:text-2xl font-semibold text-gray-800">
              Costo y Mantenimiento
            </CardTitle>
            <p className="text-base text-gray-500 mt-1">
              Conoce los gastos asociados a esta raza
            </p>
          </div>
        </div>
      </CardHeader>

      {/* Content */}
      <CardContent className="p-4 sm:p-5 space-y-6">
        {/* Gastos Iniciales */}
        {gastosIniciales && (
          <div className="bg-gradient-to-br from-emerald-50 to-white rounded-lg border border-emerald-100 p-4 sm:p-5 hover:shadow-md transition-shadow space-y-4">
            <div className="flex items-center gap-2 mb-1">
              <Package className="h-5 w-5 text-emerald-600" />
              <h3 className="text-lg sm:text-xl font-medium text-gray-900">
                Gastos Iniciales
              </h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Básicos */}
              {gastosIniciales.basicos?.length > 0 && (
                <div className="bg-white p-3 border border-emerald-50 rounded-lg hover:border-emerald-200 transition-colors space-y-2">
                  <p className="text-base font-semibold text-emerald-700">Básicos</p>
                  <BulletList items={gastosIniciales.basicos} />
                </div>
              )}
              {/* Equipamiento */}
              {gastosIniciales.equipamiento?.length > 0 && (
                <div className="bg-white p-3 border border-emerald-50 rounded-lg hover:border-emerald-200 transition-colors space-y-2">
                  <p className="text-base font-semibold text-emerald-700">Equipamiento</p>
                  <BulletList items={gastosIniciales.equipamiento} />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Gastos Mensuales */}
        {gastosMensuales && (
          <div className="bg-gradient-to-br from-emerald-50 to-white rounded-lg border border-emerald-100 p-4 sm:p-5 hover:shadow-md transition-shadow space-y-4">
            <div className="flex items-center gap-2 mb-1">
              <ShoppingCart className="h-5 w-5 text-emerald-600" />
              <h3 className="text-lg sm:text-xl font-medium text-gray-900">
                Gastos Mensuales
              </h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Fijos */}
              {gastosMensuales.fijos && (
                <div className="bg-white p-3 border border-emerald-50 rounded-lg hover:border-emerald-200 transition-colors space-y-3">
                  <p className="text-base font-semibold text-emerald-700">Fijos</p>
                  <KeyValueList data={gastosMensuales.fijos} />
                </div>
              )}
              {/* Variables */}
              {gastosMensuales.variables && (
                <div className="bg-white p-3 border border-emerald-50 rounded-lg hover:border-emerald-200 transition-colors space-y-3">
                  <p className="text-base font-semibold text-emerald-700">Variables</p>
                  <KeyValueList data={gastosMensuales.variables} />
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default CostoYMantenimientoCard;
